/**
 * Point a post at a page instead of describing it. "Ask BAM can do that"
 * becomes a card that opens Ask BAM. The list is the platform map, so it
 * is the same set of doors the tour shows and never goes stale.
 */
import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Search, X, Link2 } from "lucide-react";
import { GOLD } from "../constants/theme";
import { tourEntries } from "../constants/platformMap";
import { NAV } from "../constants/navigation";

const ICONS = NAV.reduce((m, n) => { m[n.id] = n.Icon; return m; }, {});

export function featureEmbed(entry) {
  return {
    type: "feature",
    feature_id: entry.id,
    title: entry.name,
    what: entry.what || "",
    route: entry.route,
  };
}

export function FeatureIcon({ id, size = 14, color = GOLD }) {
  const Icon = ICONS[id] || Link2;
  return <Icon size={size} color={color}/>;
}

export default function FeaturePicker({ C, onPick, onClose }) {
  const [q, setQ] = useState("");
  const all = useMemo(() => tourEntries(), []);
  const rows = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return all;
    return all.filter(e => [e.name, e.what, e.doing, e.when].filter(Boolean).join(" ").toLowerCase().includes(t));
  }, [all, q]);
  // Portaled to the body. Rendered inside a post card, a fixed overlay
  // sits inside whatever transform the card is animating, and the card's
  // own click handlers hear every click in the list.
  const stop = (e) => e.stopPropagation();
  return createPortal(
    <div onClick={(e) => { stop(e); onClose?.(); }} onMouseDown={stop} onTouchStart={stop}
      style={{ position: "fixed", inset: 0, zIndex: 6000, background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={stop} role="dialog" aria-label="Link a page"
        style={{ width: "100%", maxWidth: 520, maxHeight: "86vh", display: "flex", flexDirection: "column", background: "linear-gradient(180deg, #131313, #0A0A0A)",
          border: "1px solid rgba(226,221,159,0.30)", borderRadius: 16, boxShadow: "0 24px 70px rgba(0,0,0,0.7)", color: "#F5F5F5", fontFamily: "'DM Sans', sans-serif", overflow: "hidden" }}>
        <style>{`.bam-fp-row{background:transparent;transition:background .12s}.bam-fp-row:hover{background:rgba(255,255,255,0.055)}`}</style>
        <div style={{ padding: "18px 18px 12px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: GOLD, letterSpacing: 1.6, textTransform: "uppercase", display: "flex", alignItems: "center", gap: 6 }}><Link2 size={11}/> Link a page</div>
            <button type="button" onClick={onClose} aria-label="Close" style={{ marginLeft: "auto", background: "transparent", border: "none", color: "rgba(255,255,255,0.6)", cursor: "pointer", padding: 4, display: "flex" }}><X size={16}/></button>
          </div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", margin: "4px 0 10px" }}>A card that opens the page. Better than "go to the thing in the sidebar".</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 9, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.03)" }}>
            <Search size={14} color="rgba(255,255,255,0.5)"/>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search pages"
              style={{ flex: 1, background: "transparent", border: "none", outline: "none", color: "#F5F5F5", fontSize: 14, fontFamily: "inherit" }}/>
          </div>
        </div>
        <div style={{ overflowY: "auto", minHeight: 0 }}>
          {rows.length === 0 ? <div style={{ padding: 24, fontSize: 13, color: "rgba(255,255,255,0.5)" }}>Nothing called that.</div>
            : rows.map(e => (
              <button key={e.id} type="button" className="bam-fp-row" onClick={() => onPick(featureEmbed(e))}
                style={{ display: "flex", gap: 12, alignItems: "flex-start", width: "100%", textAlign: "left", padding: "11px 18px", border: "none", borderBottom: "1px solid rgba(255,255,255,0.05)", cursor: "pointer", color: "#F5F5F5", fontFamily: "inherit" }}>
                <span style={{ width: 30, height: 30, borderRadius: 8, background: `${GOLD}14`, border: `1px solid ${GOLD}33`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><FeatureIcon id={e.id}/></span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 700 }}>{e.name}{e.status === "beta" && <span style={{ fontSize: 9, fontWeight: 800, color: GOLD, letterSpacing: .6, textTransform: "uppercase" }}>beta</span>}</span>
                  <span style={{ display: "block", fontSize: 12.5, color: "rgba(255,255,255,0.55)", lineHeight: 1.4, marginTop: 2 }}>{e.what}</span>
                </span>
              </button>
            ))}
        </div>
      </div>
    </div>,
    document.body
  );
}
